import {
  Avatar,
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link as ChakraLink,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import QRCode from "qrcode.react";
import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import StudentPortalShell from "../../components/student/StudentPortalShell";
import DailyChallengeMenu from "../../components/student/DailyChallengeMenu";
import MissedQuestions from "../../components/student/Cards/MissedQuestions";
import UpcomingOfficeHoursPanel from "../../components/student/UpcomingOfficeHoursPanel";
import LiveSessionsPanel from "../../components/student/LiveSessionsPanel";
import HomeQuickLinksPanel from "../../components/student/HomeQuickLinksPanel";
import PoweredByAsquera from "../../components/PoweredByAsquera";
import { getAllExamsAndHomeworksRequest } from "../../modules/students-modules/exams/Actions";
import { getLiveVideoRequest } from "../../modules/students-modules/videos/Actions";
import { studentPalette } from "../../constants/studentPalette";
import { studentSurfaces } from "../../constants/studentThemeSurfaces";

const C = {
  bg: studentSurfaces.canvas,
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  border: studentSurfaces.border,
  cream: studentPalette.cream,
  paper: studentSurfaces.raised,
  warmBeige: studentPalette.warmBeige,
  muted: studentSurfaces.textMuted,
};

function scanAttendanceUrl(userId) {
  const raw = "https://edutopiaonline.com/";
  const base = String(raw)
    .replace(/\s/g, "")
    .replace(/['"]/g, "")
    .replace(/\/$/, "");
  return `${base}/scan-code/${userId}/attendence`;
}

const StudentHome = ({ t }) => {
  const baseUrl = "https://api.edutopiaonline.com/";
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const liveVideo = useSelector((state) => state.chapterVideos.liveVideo);

  const { isOpen: isChallengeOpen, onOpen: onChallengeOpen, onClose: onChallengeClose } = useDisclosure();

  useEffect(() => {
    dispatch(getLiveVideoRequest());
    dispatch(getAllExamsAndHomeworksRequest());
  }, [dispatch]);

  return (
    <StudentPortalShell>
      <Box
        bg={C.bg}
        minH={{ base: "auto", md: "100vh" }}
        py={{ base: 6, md: 10 }}
        px={{ base: 4, md: 6, lg: 8 }}
        overflowX="hidden"
        w="100%"
        maxW="100%"
        minW={0}
      >
        <Box w="100%" maxW="100%" minW={0}>
          {/* Top: dossier + live videos */}
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6} mb={8} w="full" minW={0}>
            <GridItem minW={0}>
              <Flex
                position="relative"
                direction="column"
                align="stretch"
                justify="flex-start"
                gap={5}
                p={{ base: 5, md: 6 }}
                bg={C.paper}
                borderWidth="1px"
                borderColor={C.border}
                borderRadius="lg"
                boxShadow="sm"
                w="full"
                minW={0}
                h="full"
              >
                <Flex align="flex-start" gap={4} minW={0} w="full">
                  <Avatar
                    size="lg"
                    borderRadius="lg"
                    flexShrink={0}
                    src={userData?.subject?.logo ? baseUrl + userData.subject.logo.replace(/^\//, "") : undefined}
                    name={userData?.name}
                  />
                  <Box minW={0} flex="1">
                    <Text
                      fontSize="10px"
                      fontWeight="800"
                      letterSpacing="0.16em"
                      color={`${C.secondary}99`}
                      textTransform="uppercase"
                      mb={1}
                      noOfLines={1}
                    >
                      Student dossier
                    </Text>
                    <Heading
                      size="md"
                      color={C.primary}
                      fontWeight="800"
                      mb={3}
                      fontFamily="'Tajawal','Amiri',sans-serif"
                      maxW="100%"
                      noOfLines={2}
                      overflowWrap="break-word"
                      wordBreak="normal"
                      sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
                    >
                      {userData?.name}
                    </Heading>
                    <Flex gap={{ base: 4, md: 8 }} flexWrap="wrap">
                      <Box minW={0}>
                        <Text fontSize="9px" fontWeight="700" color={`${C.secondary}66`} textTransform="uppercase" letterSpacing="wider">
                          Code
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary} noOfLines={2}>
                          {userData?.user_name || "—"}
                        </Text>
                      </Box>
                      <Box minW={0}>
                        <Text fontSize="9px" fontWeight="700" color={`${C.secondary}66`} textTransform="uppercase" letterSpacing="wider">
                          Group
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary} noOfLines={2}>
                          {userData?.group?.name || "—"}
                        </Text>
                      </Box>
                      <Box minW={0} flex="1">
                        <Text fontSize="9px" fontWeight="700" color={`${C.secondary}66`} textTransform="uppercase" letterSpacing="wider">
                          Subject
                        </Text>
                        <Text fontSize="sm" fontWeight="700" color={C.secondary} noOfLines={2}>
                          {userData?.subject?.name || "—"}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Flex>
                <VStack
                  spacing={2}
                  p={3}
                  bg={studentSurfaces.muted}
                  borderWidth="1px"
                  borderColor={C.border}
                  borderRadius="lg"
                  align="center"
                  alignSelf="center"
                  w="auto"
                  flexShrink={0}
                >
                  <Box
                    bg={C.paper}
                    p={2}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={C.border}
                  >
                    <QRCode size={104} value={scanAttendanceUrl(userData?.id)} />
                  </Box>
                  <Text fontSize="9px" fontWeight="800" color={`${C.secondary}99`} textTransform="uppercase" letterSpacing="widest">
                    Attendance QR
                  </Text>
                </VStack>
              </Flex>
            </GridItem>

            <GridItem minW={0} display="flex" flexDirection="column">
              <LiveSessionsPanel
                sessions={Array.isArray(liveVideo) ? liveVideo : []}
              />
            </GridItem>
          </Grid>

          {/* Homework, quizzes, exams, office hours */}
          <Grid
            templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
            gap={6}
            mb={10}
            w="full"
            minW={0}
            alignItems="stretch"
          >
            <GridItem minW={0} display="flex">
              <HomeQuickLinksPanel type="homework" />
            </GridItem>
            <GridItem minW={0} display="flex">
              <HomeQuickLinksPanel type="quizzes" />
            </GridItem>
            <GridItem minW={0} display="flex">
              <HomeQuickLinksPanel type="exams" />
            </GridItem>
            <GridItem minW={0} display="flex">
              <UpcomingOfficeHoursPanel />
            </GridItem>
          </Grid>

          <Box mb={10} w="full" minW={0}>
            <MissedQuestions />
          </Box>

          <Flex
            pt={8}
            pb={4}
            borderTopWidth="1px"
            borderColor={C.border}
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            gap={6}
            align={{ base: "flex-start", md: "flex-end" }}
          >
            <Box maxW="md">
              <Text fontSize="10px" fontWeight="800" letterSpacing="0.12em" color={`${C.secondary}66`} textTransform="uppercase" mb={2}>
                Edutopia student portal
              </Text>
              <Text fontSize="sm" color={C.muted} lineHeight="tall">
                Your progress and attendance tools in one place.
              </Text>
              <Box mt={4}>
                <PoweredByAsquera color={C.muted} />
              </Box>
            </Box>
            <ChakraLink
              as={Link}
              to="/privacy-policy"
              fontSize="9px"
              fontWeight="800"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color={C.secondary}
              _hover={{ color: C.primary }}
            >
              Privacy
            </ChakraLink>
          </Flex>
        </Box>
      </Box>

      <DailyChallengeMenu isOpen={isChallengeOpen} onClose={onChallengeClose} userData={userData} />
    </StudentPortalShell>
  );
};

export default withTranslation()(StudentHome);
